import React from 'react';
import * as S from './styles';
import { usePageMetadata } from '../../utils/usePageMetadata';

const featureExamples = [
  'Copying a YouTube transcript to your clipboard',
  'Copying visible video metadata',
  'Saving a video screenshot locally',
  'Hiding selected YouTube page elements',
  'Exporting visible YouTube Studio data into a local file',
  'Adjusting the YouTube viewing interface in your browser',
];

export const PrivacyPolicy: React.FC = () => {
  usePageMetadata({
    title: 'Privacy Policy | Austin Davenport',
    description: 'Privacy information for AustinDavenport.com, Creator Updates, contact forms, analytics, embedded media, external links, and Austin Davenport Chrome extensions.',
    path: '/privacy-policy',
  });

  return (
    <S.Container>
      <S.PolicyArticle>
        <S.Header>
          <S.Eyebrow>Website and Extension Privacy Policy</S.Eyebrow>
          <S.Title>Privacy Policy</S.Title>
          <S.EffectiveDate>Effective date: July 31, 2026</S.EffectiveDate>
        </S.Header>

        <S.Section>
          <p>
            This page explains how AustinDavenport.com handles information for the website,
            contact form, Creator Updates signup, analytics, embedded media, external links,
            and Chrome extensions published by Austin Davenport.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Website Information We Collect</S.SectionTitle>
          <p>
            If you submit the contact form, the information you enter, such as your name,
            email address, project type, and message, is sent through the form provider so
            Austin can respond to your inquiry.
          </p>
          <p>
            If you sign up for Creator Updates, your email address and signup source may be
            sent to Kit so Austin can send occasional updates about new tools, major product
            changes, and useful creator resources. Submitting the signup form adds your email
            to the Creator Updates list.
          </p>
          <p>
            The site uses Vercel Analytics for general usage measurement. Custom analytics
            events may record non-sensitive interaction details such as a clicked tool ID,
            category, platform, source page, or search query length. Email addresses, names,
            messages, payment information, and contact-form contents are not intentionally
            sent in analytics events.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Website Services and Embedded Content</S.SectionTitle>
          <p>
            Some website content is loaded from Supabase, including public blog, project, and
            portfolio records. YouTube embeds and thumbnails may load content directly from
            YouTube or Google. Product, support, Discord, Spotify, Adobe Exchange, Notion,
            Fourthwall, Buy Me a Coffee, and other external links take you to third-party
            websites that have their own privacy practices.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Chrome Extension Privacy Policy</S.SectionTitle>
          <p>
            The following extension privacy information applies to Chrome extensions published
            by Austin Davenport for YouTube and YouTube Studio utility workflows, including
            tools for copying transcripts, descriptions, and tags; adjusting playback; hiding
            YouTube interface elements; saving screenshots; exporting Studio analytics; and
            other small single-purpose YouTube utilities.
          </p>
          <p>
            These extensions are independent and are not affiliated with YouTube or Google.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Extension Information We Access</S.SectionTitle>
          <p>
            Our extensions may access website content on YouTube or YouTube Studio pages only
            as needed to provide the extension's stated feature. For example, an extension may
            read a visible transcript so it can copy that transcript, read visible metadata so
            it can copy tags or descriptions, capture the current video frame for a screenshot,
            or inspect page elements so it can hide or adjust parts of the YouTube interface.
          </p>
          <p>This processing happens locally in your browser.</p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Extension Information We Do Not Collect</S.SectionTitle>
          <p>We do not collect, store, sell, rent, or transfer your personal information.</p>
          <p>
            We do not collect names, email addresses, payment information, health information,
            authentication information, personal communications, precise location, browsing
            history, or user activity logs.
          </p>
          <p>
            We do not use analytics, advertising trackers, background tracking, remote logging,
            or profiling in these extensions.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>How Extension Information Is Used</S.SectionTitle>
          <p>
            Website content accessed by an extension is used only to provide that extension's
            user-facing feature.
          </p>
          <S.List aria-label="Examples of extension features">
            {featureExamples.map(example => (
              <li key={example}>{example}</li>
            ))}
          </S.List>
          <p>We do not send this information to our servers.</p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Extension Data Sharing</S.SectionTitle>
          <p>We do not sell user data.</p>
          <p>
            We do not transfer user data to advertising platforms, data brokers, or other
            third parties.
          </p>
          <p>
            We do not use or transfer user data for purposes unrelated to an extension's single
            purpose.
          </p>
          <p>
            We do not use or transfer user data to determine creditworthiness or for lending
            purposes.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Extension Remote Code</S.SectionTitle>
          <p>
            Our extensions do not use remote code. Extension code is packaged with the Chrome
            extension. External websites may be opened only when you choose to click an optional
            support or donation link.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Third-Party Links</S.SectionTitle>
          <p>
            Some extension popups may include an optional donation or support link, such as
            Buy Me a Coffee. If you click that link, you will leave the extension and visit a
            third-party website. That website's own privacy policy applies.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Data Security</S.SectionTitle>
          <p>
            Because our extensions do not transmit user data to our servers, most extension
            processing remains local to your browser. If a future extension feature ever requires
            data transmission, it will be disclosed and protected using modern secure transmission
            methods.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Chrome Web Store Limited Use Disclosure</S.SectionTitle>
          <p>
            Our use of information, including any information received from Google APIs if
            applicable, complies with the Chrome Web Store User Data Policy, including the Limited
            Use requirements.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Changes to This Policy</S.SectionTitle>
          <p>
            We may update this Privacy Policy if our extensions or privacy practices change. The
            updated version will be posted on this page with a new effective date.
          </p>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Contact</S.SectionTitle>
          <p>For privacy questions, contact:</p>
          <S.ContactBlock>
            <strong>Austin Davenport</strong>
            <a href="https://www.austindavenport.com/contact">https://www.austindavenport.com/contact</a>
          </S.ContactBlock>
        </S.Section>
      </S.PolicyArticle>
    </S.Container>
  );
};

export default PrivacyPolicy;
